import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PublishingPanel = ({ 
  selectedProducts = [], 
  totalProducts = 0,
  onStartPublishing,
  onPausePublishing,
  onResumePublishing,
  onCancelPublishing,
  isPublishing = false,
  publishingProgress = null
}) => {
  const [isConfigurationExpanded, setIsConfigurationExpanded] = useState(true);
  const wasPublishingRef = useRef(isPublishing);
  const [publishSettings, setPublishSettings] = useState({
    delayBetweenProducts: 7, // Conservative delay for bulk uploads (rate limiting protection)
    enableN8nWorkflow: false,
    selectedWorkflow: '',
    autoRetry: true,
    maxRetries: 3,
    publishMode: 'draft', // draft, active
    // Seguridad (opcionales)
    abortOnConsecutiveErrors: false,
    maxConsecutiveErrors: 10,
    abortOnFailureRate: false,
    maxFailureRate: 0.8,
    minSamplesForFailureRate: 10
  });

  useEffect(() => {
    if (isPublishing && !wasPublishingRef.current) {
      setIsConfigurationExpanded(false);
    } else if (!isPublishing && wasPublishingRef.current) {
      setIsConfigurationExpanded(true);
    }
    wasPublishingRef.current = isPublishing;
  }, [isPublishing]);

  const delayOptions = [
    { value: 3, label: '3 segundos (mínimo)' },
    { value: 5, label: '5 segundos (rápido)' },
    { value: 7, label: '7 segundos (recomendado para carga masiva)' }
  ];

  const workflowOptions = [
    { value: 'product-optimization', label: 'Optimización de Productos' },
    { value: 'price-monitoring', label: 'Monitoreo de Precios' },
    { value: 'inventory-sync', label: 'Sincronización de Inventario' },
    { value: 'competitor-analysis', label: 'Análisis de Competencia' }
  ];

  const publishModeOptions = [
    { value: 'draft', label: 'Borrador (revisar antes de publicar)' },
    { value: 'active', label: 'Publicar inmediatamente' }
  ];

  const handleSettingChange = (field, value) => {
    setPublishSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateEstimatedTime = () => {
    const productsToPublish = selectedProducts?.length || totalProducts;
    const delayPerProduct = publishSettings?.delayBetweenProducts || 7; // seconds
    // Conservative estimate: add extra time for rate limiting and API processing
    const totalSeconds = productsToPublish * (delayPerProduct + 3); // +3 seconds for API processing per product
    const totalMinutes = totalSeconds / 60;
    
    if (totalMinutes < 60) {
      return `${Math.round(totalMinutes)} minutos`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.round(totalMinutes % 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const handleStartPublishing = () => {
    onStartPublishing(publishSettings);
  };

  const validProductsCount = selectedProducts?.length || totalProducts;
  const hasValidProducts = validProductsCount > 0;

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Rocket" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              Panel de Publicación
            </h3>
            <p className="text-text-secondary">
              {validProductsCount} productos listos para publicar
            </p>
          </div>
        </div>
      </div>
      {/* Publishing Progress */}
      {isPublishing && publishingProgress && (
        <div className="p-6 border-b border-border bg-primary/5">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-text-primary">Publicando productos...</span>
              <span className="text-sm text-text-secondary">
                {publishingProgress?.completed} de {publishingProgress?.total}
              </span>
            </div>
            
            <div className="w-full bg-muted rounded-full h-3">
              <div 
                className="bg-primary h-3 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                style={{ width: `${publishingProgress?.percentage}%` }}
              >
                <span className="text-xs text-white font-medium">
                  {publishingProgress?.percentage}%
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={16} />
                <span>{publishingProgress?.successful} exitosos</span>
              </div>
              <div className="flex items-center space-x-2 text-error">
                <Icon name="XCircle" size={16} />
                <span>{publishingProgress?.failed} fallidos</span>
              </div>
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="Clock" size={16} />
                <span>ETA: {publishingProgress?.eta}</span>
              </div>
              <div className="flex items-center space-x-2 text-warning">
                <Icon name="Zap" size={16} />
                <span>{publishingProgress?.currentDelay || 7}s delay</span>
              </div>
            </div>

            {/* Rate Limiting Info */}
            {publishingProgress?.currentDelay > 10 && (
              <div className="mt-3 p-3 bg-warning/10 dark:bg-warning/20 rounded-lg border border-warning/20 dark:border-warning/40">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Shield" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-warning">Rate Limiting Activo</span>
                </div>
                <p className="text-xs text-warning/80">
                  Delay aumentado a {publishingProgress?.currentDelay}s para evitar límites de API
                  {publishingProgress?.consecutiveErrors > 0 && (
                    <span> (Errores consecutivos: {publishingProgress?.consecutiveErrors})</span>
                  )}
                </p>
              </div>
            )}

            {/* Waiting Progress */}
            {publishingProgress?.waitingMessage && (
              <div className="mt-3 p-3 bg-info/10 dark:bg-info/20 rounded-lg border border-info/20 dark:border-info/40">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={16} className="text-info" />
                  <span className="text-sm font-medium text-info">{publishingProgress?.waitingMessage}</span>
                </div>
                {publishingProgress?.waitingProgress > 0 && (
                  <div className="w-full bg-info/20 rounded-full h-2">
                    <div 
                      className="bg-info h-2 rounded-full transition-all duration-300"
                      style={{ width: `${publishingProgress?.waitingProgress}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Error Details Section */}
            {publishingProgress?.failed > 0 && publishingProgress?.errors && (
              <div className="mt-4 p-4 bg-error/10 dark:bg-error/20 rounded-lg border border-error/20 dark:border-error/40">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                  <h4 className="font-medium text-error">Errores de Publicación</h4>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {(publishingProgress?.errors || [])?.map((error, index) => (
                    <div key={index} className="text-sm text-error bg-error/5 dark:bg-error/15 p-2 rounded border-l-2 border-error dark:border-error/50">
                      {error?.productTitle ? (
                        <div>
                          <span className="font-medium">{error?.productTitle}</span>
                          <div className="text-xs text-error/80 mt-1">{error?.message}</div>
                        </div>
                      ) : (
                        <span>{error}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Settings */}
      <div className="p-6">
        <button
          type="button"
          className="flex w-full items-center justify-between text-left"
          onClick={() => setIsConfigurationExpanded(prev => !prev)}
          aria-expanded={isConfigurationExpanded}
        >
          <h4 className="font-medium text-text-primary">Configuración Individual</h4>
          <Icon name={isConfigurationExpanded ? 'ChevronDown' : 'ChevronRight'} size={18} className="text-text-secondary" />
        </button>

        <div className={isConfigurationExpanded ? 'mt-6 space-y-6' : 'hidden'}>
          {/* Individual Processing Configuration */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Delay entre Productos"
              description="Tiempo de espera entre cada producto individual"
              options={delayOptions}
              value={publishSettings?.delayBetweenProducts}
              onChange={(value) => handleSettingChange('delayBetweenProducts', value)}
            />
          </div>
          </div>

          {/* Publishing Mode */}
          <div className="space-y-4">
            <h4 className="font-medium text-text-primary">Modo de Publicación</h4>
          
            <Select
              label="Estado Inicial"
              options={publishModeOptions}
              value={publishSettings?.publishMode}
              onChange={(value) => handleSettingChange('publishMode', value)}
            />
          </div>

          {/* N8n Workflow Integration */}
          <div className="hidden space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={publishSettings?.enableN8nWorkflow}
              onChange={(e) => handleSettingChange('enableN8nWorkflow', e?.target?.checked)}
            />
            <h4 className="font-medium text-text-primary">Integración con n8n</h4>
          </div>
          
          {publishSettings?.enableN8nWorkflow && (
            <Select
              label="Flujo de Trabajo"
              description="Selecciona el workflow de n8n a ejecutar"
              options={workflowOptions}
              value={publishSettings?.selectedWorkflow}
              onChange={(value) => handleSettingChange('selectedWorkflow', value)}
              placeholder="Seleccionar workflow..."
            />
          )}
          </div>

          {/* Retry Configuration */}
          <div className="hidden space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={publishSettings?.autoRetry}
              onChange={(e) => handleSettingChange('autoRetry', e?.target?.checked)}
            />
            <h4 className="font-medium text-text-primary">Reintentos Automáticos</h4>
          </div>
          
          {publishSettings?.autoRetry && (
            <Input
              label="Máximo de Reintentos"
              type="number"
              min="1"
              max="10"
              value={publishSettings?.maxRetries}
              onChange={(e) => handleSettingChange('maxRetries', parseInt(e?.target?.value))}
            />
          )}
          </div>

          {/* Safety / Auto-cancel */}
          <div className="hidden space-y-4">
          <h4 className="font-medium text-text-primary">Seguridad</h4>

          <div className="flex items-start space-x-2">
            <Checkbox
              checked={publishSettings?.abortOnConsecutiveErrors}
              onChange={(e) => handleSettingChange('abortOnConsecutiveErrors', e?.target?.checked)}
            />
            <div className="flex-1">
              <p className="font-medium text-text-primary text-sm">Cancelar si hay demasiados errores consecutivos</p>
              <p className="text-xs text-text-secondary">Útil cuando el archivo está mal mapeado o la categoría/atributos fallan en cadena.</p>
            </div>
          </div>

          {publishSettings?.abortOnConsecutiveErrors && (
            <Input
              label="Máximo de errores consecutivos"
              type="number"
              min="1"
              max="100"
              value={publishSettings?.maxConsecutiveErrors}
              onChange={(e) => handleSettingChange('maxConsecutiveErrors', parseInt(e?.target?.value || '10'))}
            />
          )}

          <div className="flex items-start space-x-2">
            <Checkbox
              checked={publishSettings?.abortOnFailureRate}
              onChange={(e) => handleSettingChange('abortOnFailureRate', e?.target?.checked)}
            />
            <div className="flex-1">
              <p className="font-medium text-text-primary text-sm">Cancelar si la tasa de fallos es muy alta</p>
              <p className="text-xs text-text-secondary">Evita seguir si, por ejemplo, 80%+ está fallando por un mismo problema.</p>
            </div>
          </div>

          {publishSettings?.abortOnFailureRate && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tasa máxima de fallos (0.1 - 0.99)"
                type="number"
                min="0.1"
                max="0.99"
                step="0.01"
                value={publishSettings?.maxFailureRate}
                onChange={(e) => handleSettingChange('maxFailureRate', parseFloat(e?.target?.value || '0.8'))}
              />
              <Input
                label="Mínimo de productos para evaluar"
                type="number"
                min="1"
                max="200"
                value={publishSettings?.minSamplesForFailureRate}
                onChange={(e) => handleSettingChange('minSamplesForFailureRate', parseInt(e?.target?.value || '10'))}
              />
            </div>
          )}
          </div>

        </div>
      </div>

      <div className="mx-6 mb-6 flex items-center justify-between gap-4 border border-border bg-muted p-4">
        <div>
          <p className="font-medium text-text-primary">
            {isPublishing ? 'Tiempo restante estimado' : 'Tiempo estimado'}
          </p>
          <p className="text-sm text-text-secondary">
            {isPublishing ? 'Actualizado según el avance de la publicación' : 'Basado en la configuración actual'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-lg font-semibold text-primary">
            {isPublishing ? (publishingProgress?.eta || calculateEstimatedTime()) : calculateEstimatedTime()}
          </p>
          <p className="text-sm text-text-secondary">
            {isPublishing
              ? `${Math.max(0, (publishingProgress?.total || validProductsCount) - (publishingProgress?.completed || 0))} restantes`
              : `${validProductsCount} productos individuales`}
          </p>
        </div>
      </div>

      <style>{`
        .publishing-rocket-window {
          position: relative;
          display: inline-block;
          width: 34px;
          height: 34px;
          margin-right: 9px;
          overflow: hidden;
          vertical-align: middle;
        }

        .publishing-rocket-ship {
          position: absolute;
          left: 6px;
          bottom: -18px;
          width: 22px;
          height: 27px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: mercadoRocketTakeoff 1.35s
            cubic-bezier(0.45, 0, 0.35, 1) infinite;
          filter:
            drop-shadow(0 0 3px rgba(255,255,255,.55))
            drop-shadow(0 0 7px rgba(103,232,249,.65));
        }

        .publishing-rocket-flame {
          position: absolute;
          left: 9px;
          top: 21px;
          width: 5px;
          height: 12px;
          border-radius: 50% 50% 70% 70%;
          background: linear-gradient(
            to bottom,
            #fef08a 0%,
            #facc15 28%,
            #fb923c 62%,
            rgba(249,115,22,0) 100%
          );
          filter: drop-shadow(0 0 4px rgba(251,146,60,.9));
          animation: mercadoRocketFlame .16s ease-in-out infinite alternate;
          transform-origin: top center;
        }

        .publishing-active-text {
          font-weight: 700;
          letter-spacing: .15px;
          background: linear-gradient(
            90deg,
            #ffffff 0%,
            #d1fae5 45%,
            #a5f3fc 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: 0 0 12px rgba(103,232,249,.22);
          animation: mercadoPublishingGlow 1.4s ease-in-out infinite;
        }

        @keyframes mercadoRocketTakeoff {
          0% {
            transform: translateY(18px);
            opacity: 0;
          }

          10% {
            opacity: 1;
          }

          72% {
            opacity: 1;
          }

          100% {
            transform: translateY(-43px);
            opacity: 0;
          }
        }

        @keyframes mercadoRocketFlame {
          from {
            transform: scaleY(.65) scaleX(.8);
            opacity: .75;
          }

          to {
            transform: scaleY(1.25) scaleX(1);
            opacity: 1;
          }
        }

        @keyframes mercadoPublishingGlow {
          0%, 100% {
            opacity: .82;
            filter: brightness(1);
          }

          50% {
            opacity: 1;
            filter: brightness(1.16);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .publishing-rocket-ship,
          .publishing-rocket-flame,
          .publishing-active-text {
            animation: none;
          }

          .publishing-rocket-ship {
            bottom: 4px;
            opacity: 1;
          }
        }
      `}</style>

      {/* Actions */}
      <div className="p-6 border-t border-border">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <Button
            variant="primary"
            onClick={handleStartPublishing}
            disabled={!hasValidProducts || isPublishing}
            fullWidth
          >
            {isPublishing ? (
              <span className="flex items-center justify-center">
                <span
                  className="publishing-rocket-window"
                  aria-hidden="true"
                >
                  <span className="publishing-rocket-ship">
                    <Icon
                      name="Rocket"
                      size={21}
                      className="text-primary"
                    />
                  </span>
                </span>

                <span className="font-semibold text-primary">
                  Publicando...
                </span>
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Icon
                  name="Rocket"
                  size={16}
                  className="mr-2 text-white/80"
                />
                Iniciar Publicación ({validProductsCount} productos)
              </span>
            )}
          </Button>
          
          {isPublishing && (
            <>
              {publishingProgress?.isPaused ? (
                <Button
                  variant="default"
                  iconName="Play"
                  onClick={onResumePublishing}
                >
                  Reanudar
                </Button>
              ) : (
                <Button
                  variant="outline"
                  iconName="Pause"
                  onClick={onPausePublishing}
                >
                  Pausar
                </Button>
              )}
              <Button
                variant="danger"
                iconName="X"
                onClick={onCancelPublishing}
              >
                Cancelar
              </Button>
            </>
          )}
        </div>

        {!hasValidProducts && (
          <div className="mt-3 p-3 bg-warning/10 rounded-lg">
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertTriangle" size={16} />
              <span className="text-sm">
                No hay productos válidos para publicar. Revisa los errores de validación.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishingPanel;